<script setup lang="ts">
/**
 * ModuleSelectorDialog — Module picker for fitting empty slots
 *
 * Opens when user clicks an empty slot. Shows a search input that queries
 * modules compatible with the given slot type. Results are grouped by
 * groupName with 32px icons.
 *
 * Props:
 *   visible   — controls dialog visibility
 *   slotType  — which slot type to filter (high/med/low/rig)
 *   fitID     — the fit being edited (for context)
 *
 * Events:
 *   close  — when dialog should close
 *   select — when a module is selected ({ typeID, typeName })
 */
import { ref, computed, watch, nextTick } from 'vue';
import { fetchModulesForSlot, iconUrl } from '../../composables/useApi';
import type { Item, SlotType } from '../../../shared/types';

const props = defineProps<{
  visible: boolean;
  slotType: SlotType;
  fitID: number;
  shipTypeID: number;
}>();

const emit = defineEmits<{
  close: [];
  select: [payload: { typeID: number; typeName: string }];
}>();

const modules = ref<Item[]>([]);
const loading = ref(false);
const searchQuery = ref('');
const searchInput = ref<HTMLInputElement | null>(null);
const error = ref('');

let requestGeneration = 0;

const slotLabel: Record<string, string> = {
  high: 'High Slot',
  med: 'Mid Slot',
  low: 'Low Slot',
  rig: 'Rig',
  subsystem: 'Subsystem',
  service: 'Service Slot',
};

// Load the server-filtered slot/ship catalog when the dialog opens. Search is local.
watch(() => props.visible, async (v) => {
  if (v) {
    searchQuery.value = '';
    error.value = '';
    await loadModules();
    await nextTick();
    searchInput.value?.focus();
  } else {
    requestGeneration++;
    modules.value = [];
  }
});

async function loadModules(): Promise<void> {
  const generation = ++requestGeneration;
  loading.value = true;
  error.value = '';
  try {
    const results = await fetchModulesForSlot(props.slotType, props.shipTypeID);
    if (generation === requestGeneration) modules.value = results;
  } catch (e) {
    if (generation === requestGeneration) {
      error.value = e instanceof Error ? e.message : 'Failed to load modules';
      modules.value = [];
    }
  } finally {
    if (generation === requestGeneration) loading.value = false;
  }
}

const filteredModules = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  return query ? modules.value.filter(item => item.typeName.toLowerCase().includes(query)) : modules.value;
});

// Group filtered modules by groupName
const groupedModules = computed(() => {
  const groups = new Map<string, Item[]>();
  for (const item of filteredModules.value) {
    const key = item.groupName || 'Other';
    const arr = groups.get(key);
    if (arr) {
      arr.push(item);
    } else {
      groups.set(key, [item]);
    }
  }
  // Sort groups alphabetically
  return new Map([...groups.entries()].sort((a, b) => a[0].localeCompare(b[0])));
});

const totalCount = computed(() => filteredModules.value.length);

function handleSelect(item: Item): void {
  emit('select', { typeID: item.typeID, typeName: item.typeName });
  emit('close');
}

function handleKeydown(e: KeyboardEvent): void {
  if (e.key === 'Escape') {
    emit('close');
  }
}

function metaLabel(metaGroupID: number): string {
  switch (metaGroupID) {
    case 1: return 'T1';
    case 2: return 'T2';
    case 3: return 'Storyline';
    case 4: return 'Faction';
    case 5: return 'Officer';
    case 6: return 'Deadspace';
    case 14: return 'Abyssal';
    case 15: return 'Premium';
    default: return '';
  }
}

function metaClass(metaGroupID: number): string {
  switch (metaGroupID) {
    case 2: return 'meta-t2';
    case 3: return 'meta-storyline';
    case 4: return 'meta-faction';
    case 5: return 'meta-officer';
    case 6: return 'meta-deadspace';
    case 14: return 'meta-abyssal';
    default: return 'meta-t1';
  }
}
</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="visible" class="modal-overlay" @click.self="emit('close')" @keydown="handleKeydown">
        <div class="modal-dialog">
          <div class="modal-header">
            <h3 class="modal-title">Select Module — {{ slotLabel[slotType] || slotType }}</h3>
            <button class="modal-close" @click="emit('close')">&times;</button>
          </div>

          <div class="search-bar">
            <svg class="search-icon" width="14" height="14" viewBox="0 0 14 14">
              <circle cx="6" cy="6" r="4.5" fill="none" stroke="currentColor" stroke-width="1.5" />
              <line x1="9.5" y1="9.5" x2="13" y2="13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
            </svg>
            <input
              ref="searchInput"
              v-model="searchQuery"
              class="search-input"
              type="text"
              :placeholder="`Search ${slotLabel[slotType] || slotType} modules...`"
            />
            <span v-if="!loading && filteredModules.length > 0" class="result-count">{{ totalCount }}</span>
          </div>

          <div class="modal-body">
            <div v-if="loading && modules.length === 0" class="status-message">
              <div class="loading-spinner" />
              Loading modules...
            </div>

            <div v-else-if="error" class="status-message status-error">
              {{ error }}
            </div>

            <div v-else-if="filteredModules.length === 0 && searchQuery.trim()" class="status-message">
              No modules found for "{{ searchQuery }}"
            </div>

            <div v-else-if="modules.length === 0" class="status-message">
              No modules available for this slot type
            </div>

            <div v-else class="module-groups">
              <template v-for="[groupName, groupItems] in groupedModules" :key="groupName">
                <div class="group-header">{{ groupName }}</div>
                <button
                  v-for="item in groupItems"
                  :key="item.typeID"
                  class="module-item"
                  @click="handleSelect(item)"
                >
                  <img
                    :src="iconUrl(item.typeID, 64)"
                    :alt="item.typeName"
                    class="module-icon"
                    loading="lazy"
                  />
                  <span class="module-name">{{ item.typeName }}</span>
                  <span
                    v-if="metaLabel(item.metaGroupID)"
                    class="meta-badge"
                    :class="metaClass(item.metaGroupID)"
                  >
                    {{ metaLabel(item.metaGroupID) }}
                  </span>
                </button>
              </template>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.72);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.modal-dialog {
  background:
    linear-gradient(180deg, rgba(17, 24, 32, 0.98), rgba(8, 11, 16, 0.98)),
    var(--bg-panel);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg), var(--glow-panel);
  width: 560px;
  max-width: 92vw;
  max-height: 78vh;
  display: flex;
  flex-direction: column;
  animation: modal-in 0.15s ease-out;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid var(--border-color);
  background: linear-gradient(90deg, rgba(0, 212, 255, 0.08), transparent 60%);
  flex-shrink: 0;
}

.modal-title {
  font-family: var(--font-mono);
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--accent-blue);
  margin: 0;
}

.modal-close {
  background: none;
  border: 1px solid transparent;
  border-radius: var(--radius-sm);
  color: var(--text-secondary);
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0 6px;
  line-height: 1.2;
}

.modal-close:hover {
  color: var(--text-primary);
  border-color: var(--border-accent);
}

.search-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-bottom: 1px solid var(--border-color);
  flex-shrink: 0;
}

.search-icon {
  color: var(--accent-blue);
  flex-shrink: 0;
}

.search-input {
  flex: 1;
  padding: 7px 10px;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  background: var(--bg-input);
  color: var(--text-primary);
  font-size: 0.82rem;
}

.search-input:focus {
  outline: none;
  border-color: var(--accent-blue);
  box-shadow: var(--glow-blue);
}

.search-input::placeholder {
  color: var(--text-secondary);
  opacity: 0.65;
}

.result-count {
  font-family: var(--font-mono);
  font-size: 0.68rem;
  color: var(--accent-blue);
  flex-shrink: 0;
  padding: 2px 7px;
  background: rgba(0, 212, 255, 0.08);
  border: 1px solid rgba(0, 212, 255, 0.16);
  border-radius: var(--radius-sm);
}

.modal-body {
  padding: 8px 12px 12px;
  overflow-y: auto;
  min-height: 0;
  flex: 1;
}

.status-message {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-align: center;
  color: var(--text-secondary);
  font-size: 0.82rem;
  padding: 32px 0;
}

.status-error { color: var(--accent-red, #f44336); }

.loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid var(--border-color);
  border-top-color: var(--accent-blue);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

.module-groups {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.group-header {
  position: sticky;
  top: -8px;
  z-index: 1;
  padding: 7px 4px 5px;
  background: var(--bg-panel);
  color: var(--accent-blue);
  font-family: var(--font-mono);
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  border-bottom: 1px solid var(--border-color);
}

.module-item {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 42px;
  padding: 6px 8px;
  border: 1px solid transparent;
  border-radius: var(--radius-sm);
  background: rgba(0, 0, 0, 0.1);
  color: var(--text-primary);
  font-size: 0.8rem;
  cursor: pointer;
  text-align: left;
  width: 100%;
  transition: all var(--transition-fast);
}

.module-item:hover {
  background: linear-gradient(90deg, rgba(0, 212, 255, 0.075), rgba(0, 0, 0, 0.1));
  border-color: var(--border-accent);
}

.module-icon {
  width: 32px;
  height: 32px;
  border-radius: 3px;
  flex-shrink: 0;
  background: rgba(0, 0, 0, 0.2);
}

.module-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.meta-badge {
  font-family: var(--font-mono);
  font-size: 0.62rem;
  font-weight: 800;
  padding: 1px 5px;
  border-radius: 3px;
  flex-shrink: 0;
  letter-spacing: 0.04em;
}

.meta-t1 { color: var(--text-secondary); background: rgba(255, 255, 255, 0.05); }
.meta-t2 { color: #e8d882; background: rgba(232, 216, 130, 0.12); }
.meta-storyline, .meta-faction { color: #4ac4a0; background: rgba(74, 196, 160, 0.12); }
.meta-officer { color: #c78ef2; background: rgba(199, 142, 242, 0.12); }
.meta-deadspace { color: #e8a04c; background: rgba(232, 160, 76, 0.12); }
.meta-abyssal { color: #e05252; background: rgba(224, 82, 82, 0.12); }

.modal-enter-active { transition: opacity 0.2s ease; }
.modal-enter-active .modal-dialog { animation: modal-in 0.2s ease-out; }
.modal-leave-active { transition: opacity 0.15s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }

@keyframes modal-in {
  from { transform: translateY(-8px) scale(0.97); }
  to { transform: translateY(0) scale(1); }
}
</style>
